import React from "react";
import { Link, Navigate } from "react-router-dom";
import { Phone, ArrowRight, CheckCircle } from "lucide-react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import FAQSection from "@/components/FAQSection";
import { BUSINESS } from "@/constants/business";
import { getKeywordPage } from "@/constants/keywordPages";

export default function KeywordPage({ slug }) {
  const page = getKeywordPage(slug);
  if (!page) return <Navigate to="/" replace />;

  return (
    <>
      <SEO
        title={page.title}
        description={page.metaDescription}
        path={`/${page.slug}`}
        keywords={page.keywords}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: page.title,
          url: `https://vilaelevators.in/${page.slug}`,
          description: page.metaDescription,
          about: { "@id": "https://vilaelevators.in/#business" },
        }}
      />

      {/* Hero */}
      <section data-testid="keyword-page-hero" className="pt-32 pb-16 border-b border-[#e5ddd0]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">
            Vila Elevators & Escalators · Bengaluru
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1a1a1a] mb-6 leading-tight">
            {page.h1}
          </h1>
          {/* Quick answer — AEO/GEO */}
          <div data-testid="quick-answer" className="border-l-4 border-[#b08d57] bg-[#faf7f2] p-6 text-[#4a4a4a] text-lg leading-relaxed">
            {page.quickAnswer}
          </div>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="keyword-cta-quote"
              className="bg-[#b08d57] hover:bg-[#8a6a3d] text-white px-8 py-4 font-semibold uppercase tracking-wider text-sm transition-colors inline-flex items-center gap-2"
            >
              Get Free Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              className="border border-[#e5ddd0] hover:border-[#b08d57] hover:text-[#b08d57] text-[#1a1a1a] px-8 py-4 font-semibold uppercase tracking-wider text-sm transition-colors inline-flex items-center gap-2"
            >
              <Phone className="w-4 h-4" /> {BUSINESS.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Content sections */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-14">
          {page.sections.map((s, i) => (
            <div key={i}>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#1a1a1a] mb-4">{s.h2}</h2>
              <p className="text-[#4a4a4a] text-lg leading-relaxed">{s.p}</p>
            </div>
          ))}

          <div className="border border-[#e5ddd0] p-8">
            <div className="font-display text-xl font-bold text-[#1a1a1a] mb-4">Explore Vila</div>
            <ul className="grid sm:grid-cols-2 gap-3 text-[#4a4a4a]">
              {[
                { to: "/products/passenger-elevators", label: "Passenger Elevators" },
                { to: "/products/home-lifts", label: "Home Lifts" },
                { to: "/products/escalators", label: "Escalators" },
                { to: "/services", label: "AMC & Modernization" },
                { to: "/tools/elevator-cost-calculator", label: "Free Cost Calculator" },
                { to: "/elevators-bangalore", label: "Elevators in Bangalore" },
              ].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="flex items-center gap-2 hover:text-[#b08d57] transition-colors">
                    <CheckCircle className="w-4 h-4 text-[#b08d57]" /> {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQSection faqs={page.faqs} title="Common questions" />

      {/* Contact */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold tracking-tight text-[#1a1a1a] mb-3">
            Get a fixed quote within one business day
          </h2>
          <p className="text-[#6b6b6b] mb-8">Free site survey anywhere in Karnataka. No obligation.</p>
          <ContactForm sourcePage={`/${page.slug}`} />
        </div>
      </section>
    </>
  );
}
